import { sql } from "drizzle-orm";
import { pgTable, text, varchar, timestamp, jsonb, boolean, integer } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

export const users = pgTable("users", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  username: text("username").notNull().unique(),
  password: text("password"), // Optional - OIDC users don't have passwords
  email: text("email").unique(), // Optional - for OIDC users
  provider: text("provider").default("local"), // 'local' or 'oidc'
  providerId: text("provider_id"), // OIDC identifier (e.g., Replit user ID)
  firstName: text("first_name"),
  lastName: text("last_name"),
});

// Schema for local username/password registration
export const insertUserSchema = createInsertSchema(users).pick({
  username: true,
  password: true,
});

// Schema for OIDC user creation/update
export const insertOIDCUserSchema = createInsertSchema(users).pick({
  email: true,
  provider: true,
  providerId: true,
  firstName: true,
  lastName: true,
  username: true,
});

// Schema for Supabase user creation (includes email and provider info)
export const insertSupabaseUserSchema = createInsertSchema(users).pick({
  username: true,
  password: true,
  email: true,
  provider: true,
  providerId: true,
});

export type InsertUser = z.infer<typeof insertUserSchema>;
export type InsertOIDCUser = z.infer<typeof insertOIDCUserSchema>;
export type InsertSupabaseUser = z.infer<typeof insertSupabaseUserSchema>;
export type User = typeof users.$inferSelect;

export const knowledgeItems = pgTable("knowledge_items", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id),
  title: text("title").notNull(),
  content: text("content").notNull(),
  source: text("source"),
  tags: text("tags").array().default(sql`ARRAY[]::text[]`),
  type: varchar("type", { length: 20 }).notNull().default("note"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertKnowledgeItemSchema = createInsertSchema(knowledgeItems).omit({
  id: true,
  userId: true,
  createdAt: true,
});

export type InsertKnowledgeItem = z.infer<typeof insertKnowledgeItemSchema>;
export type KnowledgeItem = typeof knowledgeItems.$inferSelect;

export const sources = pgTable("sources", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().references(() => users.id),
  workspaceId: varchar("workspace_id"), // null = personal; set = workspace
  title: text("title").notNull(),
  content: text("content").notNull(),
  type: varchar("type", { length: 20 }).notNull().default("text"),
  url: text("url"),
  topics: text("topics").array().default(sql`ARRAY[]::text[]`),
  subTopics: text("sub_topics").array().default(sql`ARRAY[]::text[]`),
  tags: text("tags").array().default(sql`ARRAY[]::text[]`),
  linkedThreads: text("linked_threads").array().default(sql`ARRAY[]::text[]`),
  linkedSpaceIds: text("linked_space_ids").array().default(sql`ARRAY[]::text[]`),
  /** Set when source was linked into a space via merge (source space id). */
  mergedFromSpaceId: text("mergedFromSpaceId"),
  linkedTopicIds: text("linked_topic_ids").array().default(sql`ARRAY[]::text[]`),
  folderId: varchar("folder_id"),
  googleDriveFileId: text("google_drive_file_id"),
  googleDriveFolderId: text("google_drive_folder_id"),
  googleDriveUrl: text("google_drive_url"),
  attachments: jsonb("attachments").default(sql`'[]'::jsonb`),
  searchable: text("searchable"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertSourceSchema = createInsertSchema(sources).omit({
  id: true,
  userId: true,
  searchable: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertSource = z.infer<typeof insertSourceSchema>;
export type Source = typeof sources.$inferSelect;

// Workspaces (organizations) - optional layer; null workspace = personal account
export const workspaces = pgTable("workspaces", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  slug: text("slug"),
  createdByUserId: varchar("created_by_user_id").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertWorkspaceSchema = createInsertSchema(workspaces).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertWorkspace = z.infer<typeof insertWorkspaceSchema>;
export type Workspace = typeof workspaces.$inferSelect;

// Workspace members - workspace-level sharing
export const workspaceMembers = pgTable("workspace_members", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  userId: varchar("user_id").notNull(),
  role: varchar("role", { length: 20 }).notNull().default("member"), // owner, admin, member
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertWorkspaceMemberSchema = createInsertSchema(workspaceMembers).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertWorkspaceMember = z.infer<typeof insertWorkspaceMemberSchema>;
export type WorkspaceMember = typeof workspaceMembers.$inferSelect;

// Workspace share links (invite with editor/viewer) — mirrors space_shares pattern
export const workspaceShares = pgTable("workspace_shares", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id").notNull(),
  createdByUserId: varchar("created_by_user_id").notNull(),
  shareToken: varchar("share_token").notNull().unique(),
  accessType: varchar("access_type", { length: 20 }).notNull().default("restricted"),
  expiresAt: timestamp("expires_at"),
  isActive: varchar("is_active", { length: 5 }).notNull().default("true"),
  defaultMemberRole: varchar("default_member_role", { length: 20 }).notNull().default("viewer"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertWorkspaceShareSchema = createInsertSchema(workspaceShares).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertWorkspaceShare = z.infer<typeof insertWorkspaceShareSchema>;
export type WorkspaceShare = typeof workspaceShares.$inferSelect;

export const workspaceShareAccess = pgTable("workspace_share_access", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  shareId: varchar("share_id").notNull(),
  userId: varchar("user_id").notNull(),
  status: varchar("status", { length: 20 }).notNull().default("pending"),
  role: varchar("role", { length: 20 }).notNull().default("viewer"),
  accessedAt: timestamp("accessed_at"),
  respondedAt: timestamp("responded_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertWorkspaceShareAccessSchema = createInsertSchema(workspaceShareAccess).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertWorkspaceShareAccess = z.infer<typeof insertWorkspaceShareAccessSchema>;
export type WorkspaceShareAccess = typeof workspaceShareAccess.$inferSelect;

export const mastermindSpaces = pgTable("mastermind_spaces", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workspaceId: varchar("workspace_id"), // null = personal space; set = workspace space
  name: text("name").notNull(),
  description: text("description"),
  icon: text("icon"),
  color: text("color"),
  goals: text("goals").array().default(sql`ARRAY[]::text[]`),
  agents: jsonb("agents").default(sql`'[]'::jsonb`),
  linkedSourceIds: text("linked_source_ids").array().default(sql`ARRAY[]::text[]`),
  milestones: jsonb("milestones").default(sql`'[]'::jsonb`),
  notes: text("notes"), // Rich text notes for the space
  isLocked: varchar("is_locked", { length: 5 }).default("false"),
  lockedAt: timestamp("locked_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertMastermindSpaceSchema = createInsertSchema(mastermindSpaces).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertMastermindSpace = z.infer<typeof insertMastermindSpaceSchema>;
export type MastermindSpace = typeof mastermindSpaces.$inferSelect;

export const chatThreads = pgTable("chat_threads", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  workspaceId: varchar("workspace_id"), // null = personal; set = workspace
  title: text("title"),
  topic: text("topic"),
  topicId: varchar("topic_id"), // Reference to topics.id - links thread to a specific topic
  tags: text("tags").array().default(sql`ARRAY[]::text[]`),
  messages: jsonb("messages").default(sql`'[]'::jsonb`),
  linkedSpaceIds: text("linked_space_ids").array().default(sql`ARRAY[]::text[]`),
  /** GitHub repos attached to this thread: [{owner, name, fullName}] */
  attachedGitHubRepos: jsonb("attached_github_repos").default(sql`'[]'::jsonb`),
  /** Set when thread was linked into a space via merge (source space id). */
  mergedFromSpaceId: text("mergedFromSpaceId"),
  agentId: varchar("agent_id"), // Selected AI agent for this conversation
  contextMode: varchar("context_mode", { length: 10 }).notNull().default("MAX"), // "MAX" or "LITE"
  status: varchar("status", { length: 20 }).notNull().default("active"),
  isArchived: varchar("is_archived", { length: 5 }).default("false"),
  archivedAt: timestamp("archived_at"),
  archiveSummaryDocId: varchar("archive_summary_doc_id"), // Reference to knowledge base doc with summary
  includeInRag: boolean("includeInRag").notNull().default(true), // When false, excluded from RAG retrieval even if embedded
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertChatThreadSchema = createInsertSchema(chatThreads).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertChatThread = z.infer<typeof insertChatThreadSchema>;
export type ChatThread = typeof chatThreads.$inferSelect;

// Activity logs for tracking all system actions
export const activityLogs = pgTable("activity_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  action: varchar("action", { length: 50 }).notNull(), // "chat_created", "source_added", etc.
  entityType: varchar("entity_type", { length: 50 }).notNull(), // "chat", "source", "space", etc.
  entityId: varchar("entity_id"),
  entityTitle: text("entity_title"),
  metadata: jsonb("metadata").default(sql`'{}'::jsonb`),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertActivityLogSchema = createInsertSchema(activityLogs).omit({
  id: true,
  createdAt: true,
});

export type InsertActivityLog = z.infer<typeof insertActivityLogSchema>;
export type ActivityLog = typeof activityLogs.$inferSelect;

// AI Agents for specialized assistance
export const aiAgents = pgTable("ai_agents", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  description: text("description").notNull(),
  persona: text("persona").notNull(), // Instructions for how the agent should behave
  specialty: varchar("specialty", { length: 50 }).notNull(), // "research", "writing", "coding", "strategy", "learning"
  icon: text("icon"), // Emoji or icon identifier
  isDefault: text("is_default").notNull().default("false"), // "true" for system agents, "false" for custom
  userId: varchar("user_id"), // null for default system agents, user ID for custom agents
  integrationScopes: text("integration_scopes").array().default(sql`ARRAY[]::text[]`),
  executionMode: text("execution_mode").default("manual"),
  outputSchema: jsonb("output_schema"),
  stepRole: text("step_role"),
  actionPermissions: jsonb("action_permissions").default(sql`'{}'::jsonb`),
  confidencePolicy: jsonb("confidence_policy").default(sql`'{}'::jsonb`),
  qualityPolicy: jsonb("quality_policy").default(sql`'{}'::jsonb`),
  attachedSkillIds: text("attached_skill_ids").array().default(sql`ARRAY[]::text[]`),
  customInstructions: text("custom_instructions"),
  workerProfileId: varchar("worker_profile_id"),
  allowedToolIds: text("allowed_tool_ids").array().default(sql`ARRAY[]::text[]`),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertAiAgentSchema = createInsertSchema(aiAgents).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertAiAgent = z.infer<typeof insertAiAgentSchema>;
export type AiAgent = typeof aiAgents.$inferSelect;

// User voice/profile for AI personalization
export const userProfiles = pgTable("user_profiles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().unique(),
  
  // Core narrative fields (existing)
  writingStyle: text("writing_style"),
  expertise: text("expertise").array().default(sql`ARRAY[]::text[]`),
  communicationPreferences: text("communication_preferences"),
  worldview: text("worldview"),
  goals: text("goals").array().default(sql`ARRAY[]::text[]`),
  background: text("background"),
  
  // Structured preferences (new)
  tone: varchar("tone", { length: 50 }).default("balanced"), // professional, casual, friendly, formal, humorous, balanced
  verbosity: varchar("verbosity", { length: 50 }).default("balanced"), // concise, balanced, detailed, comprehensive
  learningStyle: varchar("learning_style", { length: 50 }).default("mixed"), // visual, auditory, reading, kinesthetic, mixed
  responseFormat: varchar("response_format", { length: 50 }).default("mixed"), // paragraphs, bullets, numbered, mixed
  
  // Advanced personalization (new)
  writingSamples: text("writing_samples").array().default(sql`ARRAY[]::text[]`), // Example writing from user
  favoritePhrases: text("favorite_phrases").array().default(sql`ARRAY[]::text[]`), // Terminology user likes
  avoidPhrases: text("avoid_phrases").array().default(sql`ARRAY[]::text[]`), // Things to avoid
  referenceAuthors: text("reference_authors").array().default(sql`ARRAY[]::text[]`), // Authors whose style user admires
  
  // Resume/CV upload
  resumeUrl: text("resume_url"), // Supabase Storage URL for uploaded resume
  resumeFileName: text("resume_file_name"), // Original filename for display
  
  // AI Response Style Preferences (detailed instructions for how AI should respond)
  responsePreferences: jsonb("response_preferences").default(sql`'{
    "includeUrls": true,
    "includeCosts": true,
    "includeActionPlans": true,
    "useBulletPoints": true,
    "includeTimelines": false,
    "includeAlternatives": true,
    "citeSources": true,
    "showConfidence": false,
    "maxParagraphLength": 3,
    "includeExamples": true,
    "includeWarnings": true,
    "customInstructions": ""
  }'::jsonb`),
  
  // Flexible storage for future enhancements
  preferences: jsonb("preferences").default(sql`'{}'::jsonb`),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertUserProfileSchema = createInsertSchema(userProfiles).omit({
  id: true,
  updatedAt: true,
});

export type InsertUserProfile = z.infer<typeof insertUserProfileSchema>;
export type UserProfile = typeof userProfiles.$inferSelect;

// Mastermind documents for rich content creation
export const mastermindDocuments = pgTable("mastermind_documents", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  workspaceId: varchar("workspace_id"), // null = personal; set = workspace
  spaceId: varchar("space_id"), // optional link to a mastermind space
  topicId: varchar("topic_id"), // optional link to a topic
  title: text("title").notNull(),
  type: varchar("type", { length: 50 }).notNull().default("note"), // "note", "report", "mind_map", "research", "article", "book", "video", "podcast", "course", "other"
  content: text("content"),
  url: text("url"), // URL for external sources
  folderId: varchar("folder_id"), // for organization
  googleDocsId: text("google_docs_id"), // optional Google Docs integration
  attachments: jsonb("attachments").default(sql`'[]'::jsonb`), // Store uploaded file metadata
  isSource: varchar("is_source", { length: 5 }).default("false"), // "true" if doc can be linked as a source
  metadata: jsonb("metadata").default(sql`'{}'::jsonb`),
  isLocked: varchar("is_locked", { length: 5 }).default("false"),
  lockedAt: timestamp("locked_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertMastermindDocumentSchema = createInsertSchema(mastermindDocuments).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertMastermindDocument = z.infer<typeof insertMastermindDocumentSchema>;
export type MastermindDocument = typeof mastermindDocuments.$inferSelect;

// Document/Library folders for organization (hierarchical, Google Drive-style)
export const documentFolders = pgTable("document_folders", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  workspaceId: varchar("workspace_id"), // null = personal; set = workspace
  name: text("name").notNull(),
  parentId: varchar("parent_id"),
  sortOrder: varchar("sort_order", { length: 20 }).default("name_asc"),
  position: varchar("position").default("0"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertDocumentFolderSchema = createInsertSchema(documentFolders).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertDocumentFolder = z.infer<typeof insertDocumentFolderSchema>;
export type DocumentFolder = typeof documentFolders.$inferSelect;

// Source entity links - junction table for many-to-many source linking
export const sourceEntityLinks = pgTable("source_entity_links", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  sourceId: varchar("source_id").notNull(),
  entityType: varchar("entity_type", { length: 20 }).notNull(),
  entityId: varchar("entity_id").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertSourceEntityLinkSchema = createInsertSchema(sourceEntityLinks).omit({
  id: true,
  createdAt: true,
});

export type InsertSourceEntityLink = z.infer<typeof insertSourceEntityLinkSchema>;
export type SourceEntityLink = typeof sourceEntityLinks.$inferSelect;

// Document linking tables for many-to-many relationships
export const documentChatThreadLinks = pgTable("document_chat_thread_links", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  documentId: varchar("document_id").notNull(),
  chatThreadId: varchar("chat_thread_id").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertDocumentChatThreadLinkSchema = createInsertSchema(documentChatThreadLinks).omit({
  id: true,
  createdAt: true,
});

export type InsertDocumentChatThreadLink = z.infer<typeof insertDocumentChatThreadLinkSchema>;
export type DocumentChatThreadLink = typeof documentChatThreadLinks.$inferSelect;

export const documentSourceLinks = pgTable("document_source_links", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  documentId: varchar("document_id").notNull(),
  sourceId: varchar("source_id").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertDocumentSourceLinkSchema = createInsertSchema(documentSourceLinks).omit({
  id: true,
  createdAt: true,
});

export type InsertDocumentSourceLink = z.infer<typeof insertDocumentSourceLinkSchema>;
export type DocumentSourceLink = typeof documentSourceLinks.$inferSelect;

export const documentKnowledgeItemLinks = pgTable("document_knowledge_item_links", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  documentId: varchar("document_id").notNull(),
  knowledgeItemId: varchar("knowledge_item_id").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertDocumentKnowledgeItemLinkSchema = createInsertSchema(documentKnowledgeItemLinks).omit({
  id: true,
  createdAt: true,
});

export type InsertDocumentKnowledgeItemLink = z.infer<typeof insertDocumentKnowledgeItemLinkSchema>;
export type DocumentKnowledgeItemLink = typeof documentKnowledgeItemLinks.$inferSelect;

// Global tags system - centralized tag management across all entities
export const tags = pgTable("tags", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(), // Creator of the tag
  name: text("name").notNull(), // Tag name (e.g., "productivity")
  color: text("color"), // Optional color for UI (e.g., "#3b82f6")
  description: text("description"), // Optional description
  category: text("category"), // Optional grouping (e.g., "work", "personal")
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertTagSchema = createInsertSchema(tags).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertTag = z.infer<typeof insertTagSchema>;
export type Tag = typeof tags.$inferSelect;

// Tag entity links - many-to-many relationships between tags and entities
export const tagEntityLinks = pgTable("tag_entity_links", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tagId: varchar("tag_id").notNull(), // References tags.id
  entityType: varchar("entity_type", { length: 50 }).notNull(), // "chat_thread", "space", "knowledge_item", "source", "document"
  entityId: varchar("entity_id").notNull(), // ID of the tagged entity
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertTagEntityLinkSchema = createInsertSchema(tagEntityLinks).omit({
  id: true,
  createdAt: true,
});

export type InsertTagEntityLink = z.infer<typeof insertTagEntityLinkSchema>;
export type TagEntityLink = typeof tagEntityLinks.$inferSelect;

// Space relationships - explicit connections between Mastermind Spaces
export const spaceRelationships = pgTable("space_relationships", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  sourceSpaceId: varchar("source_space_id").notNull(), // The space initiating the relationship
  targetSpaceId: varchar("target_space_id").notNull(), // The related space
  relationshipType: varchar("relationship_type", { length: 50 }).notNull(), // "parent", "child", "peer", "dependency"
  rationale: text("rationale"), // Why these spaces are linked (e.g., "Sleep research informs wellness goals")
  strength: varchar("strength", { length: 20 }).default("medium"), // "weak", "medium", "strong"
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertSpaceRelationshipSchema = createInsertSchema(spaceRelationships).omit({
  id: true,
  createdAt: true,
});

export type InsertSpaceRelationship = z.infer<typeof insertSpaceRelationshipSchema>;
export type SpaceRelationship = typeof spaceRelationships.$inferSelect;

// Space assets - images, hero media, and visual customization
export const spaceAssets = pgTable("space_assets", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  spaceId: varchar("space_id").notNull(), // References mastermind_spaces.id
  assetType: varchar("asset_type", { length: 50 }).notNull(), // "hero_image", "icon", "background"
  url: text("url").notNull(), // URL to the asset (object storage or external)
  metadata: jsonb("metadata").default(sql`'{}'::jsonb`), // Additional data (dimensions, alt text, etc.)
  isPrimary: varchar("is_primary", { length: 5 }).default("false"), // "true" or "false" - primary asset for this type
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertSpaceAssetSchema = createInsertSchema(spaceAssets).omit({
  id: true,
  createdAt: true,
});

export type InsertSpaceAsset = z.infer<typeof insertSpaceAssetSchema>;
export type SpaceAsset = typeof spaceAssets.$inferSelect;

// Topics - organizational layer within Mastermind Spaces
export const topics = pgTable("topics", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  spaceId: varchar("space_id").notNull(), // References mastermind_spaces.id
  workspaceId: varchar("workspace_id"), // null = personal; set = workspace (denormalized from space)
  name: text("name").notNull(),
  description: text("description"),
  color: text("color"), // Optional color for UI visualization
  order: varchar("order", { length: 10 }).default("0"), // For custom ordering
  isLocked: varchar("is_locked", { length: 5 }).default("false"),
  lockedAt: timestamp("locked_at"),
  /** User who created the topic (defaults to space owner for legacy rows) */
  createdByUserId: varchar("created_by_user_id"),
  /** Set when topic was moved into a space via merge (source space id). */
  mergedFromSpaceId: text("merged_from_space_id"),
  /** Parent topic id for nested subtopics; null for top-level topics. */
  parentTopicId: varchar("parent_id"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertTopicSchema = createInsertSchema(topics).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertTopic = z.infer<typeof insertTopicSchema>;
export type Topic = typeof topics.$inferSelect;

// Subtopics - hierarchical organization under topics
export const subtopics = pgTable("subtopics", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  topicId: varchar("topic_id").notNull(), // References topics.id
  name: text("name").notNull(),
  description: text("description"),
  order: varchar("order", { length: 10 }).default("0"), // For custom ordering
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertSubtopicSchema = createInsertSchema(subtopics).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertSubtopic = z.infer<typeof insertSubtopicSchema>;
export type Subtopic = typeof subtopics.$inferSelect;

// Thread-Space links - many-to-many relationships for threads across multiple spaces
export const threadSpaceLinks = pgTable("thread_space_links", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  threadId: varchar("thread_id").notNull(), // References chat_threads.id
  spaceId: varchar("space_id").notNull(), // References mastermind_spaces.id
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertThreadSpaceLinkSchema = createInsertSchema(threadSpaceLinks).omit({
  id: true,
  createdAt: true,
});

export type InsertThreadSpaceLink = z.infer<typeof insertThreadSpaceLinkSchema>;
export type ThreadSpaceLink = typeof threadSpaceLinks.$inferSelect;

// Document-Space links - many-to-many relationships for documents across multiple spaces
export const documentSpaceLinks = pgTable("document_space_links", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  documentId: varchar("document_id").notNull(), // References mastermind_documents.id
  spaceId: varchar("space_id").notNull(), // References mastermind_spaces.id
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertDocumentSpaceLinkSchema = createInsertSchema(documentSpaceLinks).omit({
  id: true,
  createdAt: true,
});

export type InsertDocumentSpaceLink = z.infer<typeof insertDocumentSpaceLinkSchema>;
export type DocumentSpaceLink = typeof documentSpaceLinks.$inferSelect;

// Source-Space links - many-to-many relationships for sources across multiple spaces
// @deprecated - Use document_entity_links instead. Sources are now unified into documents with isSource=true
export const sourceSpaceLinks = pgTable("source_space_links", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  sourceId: varchar("source_id").notNull(), // References sources.id
  spaceId: varchar("space_id").notNull(), // References mastermind_spaces.id
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertSourceSpaceLinkSchema = createInsertSchema(sourceSpaceLinks).omit({
  id: true,
  createdAt: true,
});

export type InsertSourceSpaceLink = z.infer<typeof insertSourceSpaceLinkSchema>;
export type SourceSpaceLink = typeof sourceSpaceLinks.$inferSelect;

// Document-Entity links - unified bidirectional linking for source documents to spaces, topics, threads
// A document marked as isSource=true can be linked to multiple entities
export const documentEntityLinks = pgTable("document_entity_links", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  documentId: varchar("document_id").notNull(), // References mastermind_documents.id
  entityType: varchar("entity_type", { length: 50 }).notNull(), // "space", "topic", "thread"
  entityId: varchar("entity_id").notNull(), // ID of the linked entity
  linkType: varchar("link_type", { length: 50 }).default("reference"), // "reference", "context", "summary"
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertDocumentEntityLinkSchema = createInsertSchema(documentEntityLinks).omit({
  id: true,
  createdAt: true,
});

export type InsertDocumentEntityLink = z.infer<typeof insertDocumentEntityLinkSchema>;
export type DocumentEntityLink = typeof documentEntityLinks.$inferSelect;

// Space Shares - shareable links for Mastermind Spaces
export const spaceShares = pgTable("space_shares", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  spaceId: varchar("space_id").notNull(), // References mastermind_spaces.id
  createdByUserId: varchar("created_by_user_id").notNull(), // User who created the share link
  shareToken: varchar("share_token").notNull().unique(), // Unique token for the shareable link
  accessType: varchar("access_type", { length: 20 }).notNull().default("restricted"), // "restricted", "anyone", "organization"
  expiresAt: timestamp("expires_at"), // Optional expiration date
  isActive: varchar("is_active", { length: 5 }).notNull().default("true"), // "true" or "false"
  /** Default role (editor | viewer) assigned to users who accept this share link */
  defaultMemberRole: varchar("default_member_role", { length: 20 }).notNull().default("viewer"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertSpaceShareSchema = createInsertSchema(spaceShares).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertSpaceShare = z.infer<typeof insertSpaceShareSchema>;
export type SpaceShare = typeof spaceShares.$inferSelect;

// Space Share Access - tracks user access and acceptance status
export const spaceShareAccess = pgTable("space_share_access", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  shareId: varchar("share_id").notNull(), // References space_shares.id
  userId: varchar("user_id").notNull(), // User who was granted access
  status: varchar("status", { length: 20 }).notNull().default("pending"), // "pending", "accepted", "rejected"
  /** editor | viewer — inherited permissions for shared space content */
  role: varchar("role", { length: 20 }).notNull().default("viewer"),
  accessedAt: timestamp("accessed_at"), // When user first accessed the link
  respondedAt: timestamp("responded_at"), // When user accepted/rejected
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

/** Per-item access override within a shared space (set by space owner) */
export const spaceItemPermissions = pgTable("space_item_permissions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  spaceId: varchar("space_id").notNull(),
  itemType: varchar("item_type", { length: 20 }).notNull(), // topic | thread | document
  itemId: varchar("item_id").notNull(),
  subjectUserId: varchar("subject_user_id").notNull(),
  accessLevel: varchar("access_level", { length: 20 }).notNull(), // editor | viewer
  grantedByUserId: varchar("granted_by_user_id").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertSpaceShareAccessSchema = createInsertSchema(spaceShareAccess).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertSpaceShareAccess = z.infer<typeof insertSpaceShareAccessSchema>;
export type SpaceShareAccess = typeof spaceShareAccess.$inferSelect;

export const insertSpaceItemPermissionSchema = createInsertSchema(spaceItemPermissions).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertSpaceItemPermission = z.infer<typeof insertSpaceItemPermissionSchema>;
export type SpaceItemPermission = typeof spaceItemPermissions.$inferSelect;

// Agent Shares - shareable links for AI Agents (same pattern as space_shares)
export const agentShares = pgTable("agent_shares", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  agentId: varchar("agent_id").notNull(), // References ai_agents.id
  createdByUserId: varchar("created_by_user_id").notNull(),
  shareToken: varchar("share_token").notNull().unique(),
  accessType: varchar("access_type", { length: 20 }).notNull().default("restricted"),
  expiresAt: timestamp("expires_at"),
  isActive: varchar("is_active", { length: 5 }).notNull().default("true"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertAgentShareSchema = createInsertSchema(agentShares).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertAgentShare = z.infer<typeof insertAgentShareSchema>;
export type AgentShare = typeof agentShares.$inferSelect;

// Agent Share Access - tracks user access and acceptance status
export const agentShareAccess = pgTable("agent_share_access", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  shareId: varchar("share_id").notNull(), // References agent_shares.id
  userId: varchar("user_id").notNull(),
  status: varchar("status", { length: 20 }).notNull().default("pending"),
  accessedAt: timestamp("accessed_at"),
  respondedAt: timestamp("responded_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertAgentShareAccessSchema = createInsertSchema(agentShareAccess).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertAgentShareAccess = z.infer<typeof insertAgentShareAccessSchema>;
export type AgentShareAccess = typeof agentShareAccess.$inferSelect;

// Agent Folders - organize custom AI agents
export const agentFolders = pgTable("agent_folders", {
  id: varchar("id").primaryKey(),
  userId: varchar("user_id").notNull(),
  name: varchar("name").notNull(),
  parentId: varchar("parent_id"),
  sortOrder: integer("sort_order").notNull().default(0),
  workspaceId: varchar("workspace_id"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export type AgentFolder = typeof agentFolders.$inferSelect;

// Agent Folder Shares - collective share links for folders of agents
export const agentFolderShares = pgTable("agent_folder_shares", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  folderId: varchar("folder_id").notNull(),
  createdByUserId: varchar("created_by_user_id").notNull(),
  shareToken: varchar("share_token").notNull().unique(),
  accessType: varchar("access_type", { length: 20 }).notNull().default("restricted"),
  expiresAt: timestamp("expires_at"),
  isActive: varchar("is_active", { length: 5 }).notNull().default("true"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export type AgentFolderShare = typeof agentFolderShares.$inferSelect;

export const agentFolderShareAccess = pgTable("agent_folder_share_access", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  shareId: varchar("share_id").notNull(),
  userId: varchar("user_id").notNull(),
  status: varchar("status", { length: 20 }).notNull().default("pending"),
  accessedAt: timestamp("accessed_at"),
  respondedAt: timestamp("responded_at"),
  copiedFolderId: varchar("copied_folder_id"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export type AgentFolderShareAccess = typeof agentFolderShareAccess.$inferSelect;

// Thread Shares - shareable links for Chat Threads
export const threadShares = pgTable("thread_shares", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  threadId: varchar("thread_id").notNull(), // References chat_threads.id
  createdByUserId: varchar("created_by_user_id").notNull(),
  shareToken: varchar("share_token").notNull().unique(),
  accessType: varchar("access_type", { length: 20 }).notNull().default("restricted"),
  expiresAt: timestamp("expires_at"),
  isActive: varchar("is_active", { length: 5 }).notNull().default("true"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertThreadShareSchema = createInsertSchema(threadShares).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertThreadShare = z.infer<typeof insertThreadShareSchema>;
export type ThreadShare = typeof threadShares.$inferSelect;

// Thread Share Access - tracks user access and acceptance status
export const threadShareAccess = pgTable("thread_share_access", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  shareId: varchar("share_id").notNull(),
  userId: varchar("user_id").notNull(),
  status: varchar("status", { length: 20 }).notNull().default("pending"),
  accessedAt: timestamp("accessed_at"),
  respondedAt: timestamp("responded_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertThreadShareAccessSchema = createInsertSchema(threadShareAccess).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertThreadShareAccess = z.infer<typeof insertThreadShareAccessSchema>;
export type ThreadShareAccess = typeof threadShareAccess.$inferSelect;

// Topic Shares - shareable links for Topics
export const topicShares = pgTable("topic_shares", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  topicId: varchar("topic_id").notNull(), // References topics.id
  createdByUserId: varchar("created_by_user_id").notNull(),
  shareToken: varchar("share_token").notNull().unique(),
  accessType: varchar("access_type", { length: 20 }).notNull().default("restricted"),
  expiresAt: timestamp("expires_at"),
  isActive: varchar("is_active", { length: 5 }).notNull().default("true"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertTopicShareSchema = createInsertSchema(topicShares).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertTopicShare = z.infer<typeof insertTopicShareSchema>;
export type TopicShare = typeof topicShares.$inferSelect;

// Topic Share Access - tracks user access and acceptance status
export const topicShareAccess = pgTable("topic_share_access", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  shareId: varchar("share_id").notNull(),
  userId: varchar("user_id").notNull(),
  status: varchar("status", { length: 20 }).notNull().default("pending"),
  accessedAt: timestamp("accessed_at"),
  respondedAt: timestamp("responded_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertTopicShareAccessSchema = createInsertSchema(topicShareAccess).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertTopicShareAccess = z.infer<typeof insertTopicShareAccessSchema>;
export type TopicShareAccess = typeof topicShareAccess.$inferSelect;

// Document Shares - public read-only links for knowledgebase notes
export const documentShares = pgTable("document_shares", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  documentId: varchar("document_id").notNull(),
  createdByUserId: varchar("created_by_user_id").notNull(),
  shareToken: varchar("share_token").notNull().unique(),
  expiresAt: timestamp("expires_at"),
  isActive: varchar("is_active", { length: 5 }).notNull().default("true"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertDocumentShareSchema = createInsertSchema(documentShares).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertDocumentShare = z.infer<typeof insertDocumentShareSchema>;
export type DocumentShare = typeof documentShares.$inferSelect;

// ============================================================================
// CREDIT TRACKING & MONETIZATION SYSTEM
// ============================================================================

// Subscription Plans - defines available tiers (Free, Pro, Enterprise)
export const subscriptionPlans = pgTable("subscription_plans", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull().unique(), // "Free", "Pro", "Enterprise"
  displayName: text("display_name").notNull(), // "DEEPLY SMART Pro"
  description: text("description"),
  monthlyCredits: varchar("monthly_credits").notNull().default("1000"), // Credits per month
  price: varchar("price").notNull().default("0"), // Price in cents (0 for Free, 2000 for $20/mo)
  features: jsonb("features").default(sql`'[]'::jsonb`), // List of feature descriptions
  isActive: varchar("is_active", { length: 10 }).notNull().default("true"), // "true" or "false"
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertSubscriptionPlanSchema = createInsertSchema(subscriptionPlans).omit({
  id: true,
  createdAt: true,
});

export type InsertSubscriptionPlan = z.infer<typeof insertSubscriptionPlanSchema>;
export type SubscriptionPlan = typeof subscriptionPlans.$inferSelect;

// User Credits - tracks current balance for each user
export const userCredits = pgTable("user_credits", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().unique(), // References users.id
  balance: varchar("balance").notNull().default("0"), // Current credit balance
  totalEarned: varchar("total_earned").notNull().default("0"), // Total credits earned (purchases + bonuses)
  totalSpent: varchar("total_spent").notNull().default("0"), // Total credits spent on AI usage
  lastResetAt: timestamp("last_reset_at"), // When monthly credits were last reset
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertUserCreditsSchema = createInsertSchema(userCredits).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertUserCredits = z.infer<typeof insertUserCreditsSchema>;
export type UserCredits = typeof userCredits.$inferSelect;

// Credit Transactions - logs every credit change
export const creditTransactions = pgTable("credit_transactions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(), // References users.id
  type: varchar("type", { length: 20 }).notNull(), // "debit", "credit", "reset"
  amount: varchar("amount").notNull(), // Amount of credits changed (positive for credit, negative for debit)
  balanceBefore: varchar("balance_before").notNull(), // Balance before transaction
  balanceAfter: varchar("balance_after").notNull(), // Balance after transaction
  reason: varchar("reason", { length: 50 }).notNull(), // "chat_request", "monthly_reset", "purchase", "bonus"
  metadata: jsonb("metadata").default(sql`'{}'::jsonb`), // Additional context (model, tokens, etc.)
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertCreditTransactionSchema = createInsertSchema(creditTransactions).omit({
  id: true,
  createdAt: true,
});

export type InsertCreditTransaction = z.infer<typeof insertCreditTransactionSchema>;
export type CreditTransaction = typeof creditTransactions.$inferSelect;

// User Subscriptions - links users to subscription plans
export const userSubscriptions = pgTable("user_subscriptions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().unique(), // References users.id
  planId: varchar("plan_id").notNull(), // References subscription_plans.id
  status: varchar("status", { length: 20 }).notNull().default("active"), // "active", "cancelled", "expired"
  currentPeriodStart: timestamp("current_period_start").notNull().defaultNow(),
  currentPeriodEnd: timestamp("current_period_end"),
  cancelAtPeriodEnd: varchar("cancel_at_period_end", { length: 10 }).notNull().default("false"),
  stripeCustomerId: text("stripe_customer_id"), // For future Stripe integration
  stripeSubscriptionId: text("stripe_subscription_id"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertUserSubscriptionSchema = createInsertSchema(userSubscriptions).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertUserSubscription = z.infer<typeof insertUserSubscriptionSchema>;
export type UserSubscription = typeof userSubscriptions.$inferSelect;

// Promotional coupons (redeemed via RPC `redeem_coupon`; admin CRUD later)
export const coupons = pgTable("coupons", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  code: text("code").notNull().unique(),
  couponType: varchar("coupon_type", { length: 20 }).notNull(), // single_use | multi_use
  bonusCredits: integer("bonus_credits").notNull().default(1500),
  maxRedemptions: integer("max_redemptions"), // null = unlimited (multi_use)
  redemptionCount: integer("redemption_count").notNull().default(0),
  startsAt: timestamp("starts_at", { withTimezone: true }),
  expiresAt: timestamp("expires_at", { withTimezone: true }),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
});

export const couponRedemptions = pgTable("coupon_redemptions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  couponId: varchar("coupon_id").notNull(),
  userId: varchar("user_id").notNull(),
  creditsGranted: integer("credits_granted").notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
});

export type Coupon = typeof coupons.$inferSelect;
export type CouponRedemption = typeof couponRedemptions.$inferSelect;

// GitHub Integrations - stores OAuth tokens for GitHub
export const githubIntegrations = pgTable("github_integrations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(), // References auth.users(id)
  accessToken: text("access_token").notNull(),
  tokenType: varchar("token_type", { length: 20 }).notNull().default("bearer"),
  scope: text("scope"),
  githubUserId: text("github_user_id"),
  githubUsername: text("github_username"),
  githubAvatarUrl: text("github_avatar_url"),
  expiresAt: timestamp("expires_at"),
  refreshToken: text("refresh_token"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertGitHubIntegrationSchema = createInsertSchema(githubIntegrations).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertGitHubIntegration = z.infer<typeof insertGitHubIntegrationSchema>;
export type GitHubIntegration = typeof githubIntegrations.$inferSelect;

// ============================================================================
// LIFE COMMAND CENTER - VITAL 7 WELLNESS TRACKING
// ============================================================================

// Vital 7 Wellness Scores - daily wellness tracking across 7 domains + identity capstone
export const wellnessScores = pgTable("wellness_scores", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  date: timestamp("date").notNull().defaultNow(), // Date of the wellness check-in
  physical: varchar("physical", { length: 2 }).default("5"), // 1-10 scale
  emotional: varchar("emotional", { length: 2 }).default("5"),
  spiritual: varchar("spiritual", { length: 2 }).default("5"),
  mental: varchar("mental", { length: 2 }).default("5"),
  relational: varchar("relational", { length: 2 }).default("5"),
  financial: varchar("financial", { length: 2 }).default("5"),
  vocational: varchar("vocational", { length: 2 }).default("5"),
  whoAmI: varchar("who_am_i", { length: 2 }).default("5"), // Identity capstone score
  notes: text("notes"), // Optional reflection notes
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertWellnessScoreSchema = createInsertSchema(wellnessScores).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertWellnessScore = z.infer<typeof insertWellnessScoreSchema>;
export type WellnessScore = typeof wellnessScores.$inferSelect;

// ============================================================================
// LIFE COMMAND CENTER - IDENTITY MODULE
// ============================================================================

// Identity Creed - user's personal confession/mission statement
export const identityCreed = pgTable("identity_creed", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().unique(),
  title: text("title").default("My Identity Creed"),
  content: text("content"), // Rich text content of the creed
  coreValues: text("core_values").array().default(sql`ARRAY[]::text[]`), // Top values
  strengths: text("strengths").array().default(sql`ARRAY[]::text[]`), // Key strengths
  purpose: text("purpose"), // Life purpose statement
  vision: text("vision"), // Future vision
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertIdentityCreedSchema = createInsertSchema(identityCreed).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertIdentityCreed = z.infer<typeof insertIdentityCreedSchema>;
export type IdentityCreed = typeof identityCreed.$inferSelect;

// Legacy Compass - long-term direction and legacy planning
export const legacyCompass = pgTable("legacy_compass", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().unique(),
  legacyStatement: text("legacy_statement"), // What legacy do you want to leave?
  impactAreas: text("impact_areas").array().default(sql`ARRAY[]::text[]`), // Areas of impact
  milestones: jsonb("milestones").default(sql`'[]'::jsonb`), // Major life milestones
  mentors: text("mentors").array().default(sql`ARRAY[]::text[]`), // People who inspire
  contributions: jsonb("contributions").default(sql`'[]'::jsonb`), // Ways to contribute
  timeHorizons: jsonb("time_horizons").default(sql`'{}'::jsonb`), // 1yr, 5yr, 10yr, lifetime goals
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertLegacyCompassSchema = createInsertSchema(legacyCompass).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertLegacyCompass = z.infer<typeof insertLegacyCompassSchema>;
export type LegacyCompass = typeof legacyCompass.$inferSelect;

// ============================================================================
// LIFE COMMAND CENTER - GAMIFICATION SYSTEM
// ============================================================================

// User Gamification Stats - streaks, XP, levels
export const userGameStats = pgTable("user_game_stats", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().unique(),
  xp: varchar("xp").notNull().default("0"), // Experience points
  level: varchar("level").notNull().default("1"), // Current level
  currentStreak: varchar("current_streak").notNull().default("0"), // Days in current streak
  longestStreak: varchar("longest_streak").notNull().default("0"), // Best streak ever
  lastActiveDate: timestamp("last_active_date"), // For streak tracking
  totalSessions: varchar("total_sessions").notNull().default("0"), // Total learning sessions
  totalCardsReviewed: varchar("total_cards_reviewed").notNull().default("0"),
  totalQuizzesTaken: varchar("total_quizzes_taken").notNull().default("0"),
  totalWorkoutsCompleted: varchar("total_workouts_completed").notNull().default("0"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertUserGameStatsSchema = createInsertSchema(userGameStats).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertUserGameStats = z.infer<typeof insertUserGameStatsSchema>;
export type UserGameStats = typeof userGameStats.$inferSelect;

// Achievements - unlockable badges and milestones
export const achievements = pgTable("achievements", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  description: text("description").notNull(),
  icon: text("icon"), // Emoji or icon identifier
  category: varchar("category", { length: 50 }).notNull(), // "learning", "wellness", "streak", "milestone"
  requirement: jsonb("requirement").default(sql`'{}'::jsonb`), // Conditions to unlock
  xpReward: varchar("xp_reward").notNull().default("10"),
  isSecret: varchar("is_secret", { length: 5 }).default("false"), // Hidden until unlocked
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertAchievementSchema = createInsertSchema(achievements).omit({
  id: true,
  createdAt: true,
});

export type InsertAchievement = z.infer<typeof insertAchievementSchema>;
export type Achievement = typeof achievements.$inferSelect;

// User Achievements - tracks which achievements each user has unlocked
export const userAchievements = pgTable("user_achievements", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  achievementId: varchar("achievement_id").notNull(),
  unlockedAt: timestamp("unlocked_at").notNull().defaultNow(),
  progress: varchar("progress").default("100"), // Percentage for progressive achievements
});

export const insertUserAchievementSchema = createInsertSchema(userAchievements).omit({
  id: true,
});

export type InsertUserAchievement = z.infer<typeof insertUserAchievementSchema>;
export type UserAchievement = typeof userAchievements.$inferSelect;

// Learning Queue - research/learning tasks to accomplish
export const learningQueue = pgTable("learning_queue", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  title: text("title").notNull(),
  description: text("description"),
  sourceId: varchar("source_id"), // Optional link to a source
  spaceId: varchar("space_id"), // Optional link to a space
  topicId: varchar("topic_id"), // Optional link to a topic
  priority: varchar("priority", { length: 10 }).notNull().default("medium"), // "low", "medium", "high", "urgent"
  status: varchar("status", { length: 20 }).notNull().default("pending"), // "pending", "in_progress", "completed"
  dueDate: timestamp("due_date"),
  completedAt: timestamp("completed_at"),
  xpReward: varchar("xp_reward").default("5"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertLearningQueueSchema = createInsertSchema(learningQueue).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertLearningQueueItem = z.infer<typeof insertLearningQueueSchema>;
export type LearningQueueItem = typeof learningQueue.$inferSelect;

// ============================================================================
// LIFE COMMAND CENTER - FLASHCARD SYSTEM
// ============================================================================

// Flashcards - spaced repetition learning cards
export const flashcards = pgTable("flashcards", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  front: text("front").notNull(), // Question or term
  back: text("back").notNull(), // Answer or definition
  sourceType: varchar("source_type", { length: 20 }), // "knowledge_base", "source", "manual", "ai_generated"
  sourceId: varchar("source_id"), // Reference to original content
  spaceId: varchar("space_id"), // Optional link to a space
  topicId: varchar("topic_id"), // Optional link to a topic
  tags: text("tags").array().default(sql`ARRAY[]::text[]`),
  difficulty: varchar("difficulty", { length: 10 }).default("medium"), // "easy", "medium", "hard"
  // Spaced repetition fields
  interval: varchar("interval").default("1"), // Days until next review
  easeFactor: varchar("ease_factor").default("2.5"), // SM-2 algorithm ease factor
  repetitions: varchar("repetitions").default("0"), // Times successfully recalled
  nextReviewDate: timestamp("next_review_date").defaultNow(),
  lastReviewDate: timestamp("last_review_date"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertFlashcardSchema = createInsertSchema(flashcards).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertFlashcard = z.infer<typeof insertFlashcardSchema>;
export type Flashcard = typeof flashcards.$inferSelect;

// Flashcard Reviews - tracks each review session
export const flashcardReviews = pgTable("flashcard_reviews", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  flashcardId: varchar("flashcard_id").notNull(),
  quality: varchar("quality", { length: 1 }).notNull(), // 0-5 SM-2 quality rating
  responseTime: varchar("response_time"), // Milliseconds to respond
  wasCorrect: varchar("was_correct", { length: 5 }).notNull(), // "true" or "false"
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertFlashcardReviewSchema = createInsertSchema(flashcardReviews).omit({
  id: true,
  createdAt: true,
});

export type InsertFlashcardReview = z.infer<typeof insertFlashcardReviewSchema>;
export type FlashcardReview = typeof flashcardReviews.$inferSelect;

// ============================================================================
// LIFE COMMAND CENTER - MIND WORKOUT SYSTEM
// ============================================================================

// Mind Workout Sessions - tracks workout sessions
export const mindWorkoutSessions = pgTable("mind_workout_sessions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  sessionType: varchar("session_type", { length: 30 }).notNull(), // "faq", "did_you_know", "quiz", "word_power", "mixed"
  spaceId: varchar("space_id"), // Optional focus area
  topicId: varchar("topic_id"),
  score: varchar("score"), // Session score if applicable
  questionsAnswered: varchar("questions_answered").default("0"),
  correctAnswers: varchar("correct_answers").default("0"),
  xpEarned: varchar("xp_earned").default("0"),
  durationSeconds: varchar("duration_seconds"),
  completedAt: timestamp("completed_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertMindWorkoutSessionSchema = createInsertSchema(mindWorkoutSessions).omit({
  id: true,
  createdAt: true,
});

export type InsertMindWorkoutSession = z.infer<typeof insertMindWorkoutSessionSchema>;
export type MindWorkoutSession = typeof mindWorkoutSessions.$inferSelect;

// Vocabulary Words - Word Power vocabulary builder
export const vocabularyWords = pgTable("vocabulary_words", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  word: text("word").notNull(),
  definition: text("definition").notNull(),
  partOfSpeech: varchar("part_of_speech", { length: 20 }), // "noun", "verb", "adjective", etc.
  pronunciation: text("pronunciation"),
  exampleSentence: text("example_sentence"),
  etymology: text("etymology"),
  synonyms: text("synonyms").array().default(sql`ARRAY[]::text[]`),
  antonyms: text("antonyms").array().default(sql`ARRAY[]::text[]`),
  sourceContext: text("source_context"), // Where the word was encountered
  spaceId: varchar("space_id"), // Related space
  topicId: varchar("topic_id"), // Related topic
  masteryLevel: varchar("mastery_level", { length: 10 }).default("new"), // "new", "learning", "familiar", "mastered"
  reviewCount: varchar("review_count").default("0"),
  lastReviewDate: timestamp("last_review_date"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertVocabularyWordSchema = createInsertSchema(vocabularyWords).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertVocabularyWord = z.infer<typeof insertVocabularyWordSchema>;
export type VocabularyWord = typeof vocabularyWords.$inferSelect;

// Quiz Results - tracks quiz performance
export const quizResults = pgTable("quiz_results", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  sessionId: varchar("session_id"), // Links to mind_workout_sessions
  quizType: varchar("quiz_type", { length: 30 }).notNull(), // "multiple_choice", "true_false", "fill_blank"
  question: text("question").notNull(),
  correctAnswer: text("correct_answer").notNull(),
  userAnswer: text("user_answer"),
  wasCorrect: varchar("was_correct", { length: 5 }).notNull(), // "true" or "false"
  spaceId: varchar("space_id"),
  topicId: varchar("topic_id"),
  difficulty: varchar("difficulty", { length: 10 }).default("medium"),
  responseTimeMs: varchar("response_time_ms"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertQuizResultSchema = createInsertSchema(quizResults).omit({
  id: true,
  createdAt: true,
});

export type InsertQuizResult = z.infer<typeof insertQuizResultSchema>;
export type QuizResult = typeof quizResults.$inferSelect;

// Did You Know Facts - stores interesting facts for the user
export const didYouKnowFacts = pgTable("did_you_know_facts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  fact: text("fact").notNull(),
  source: text("source"), // Where the fact came from
  spaceId: varchar("space_id"),
  topicId: varchar("topic_id"),
  isFavorite: varchar("is_favorite", { length: 5 }).default("false"),
  timesShown: varchar("times_shown").default("0"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertDidYouKnowFactSchema = createInsertSchema(didYouKnowFacts).omit({
  id: true,
  createdAt: true,
});

export type InsertDidYouKnowFact = z.infer<typeof insertDidYouKnowFactSchema>;
export type DidYouKnowFact = typeof didYouKnowFacts.$inferSelect;

// User Edition Selection - tracks which edition template user selected
export const userEditions = pgTable("user_editions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull().unique(),
  editionId: varchar("edition_id").notNull(), // "student", "business", "creative", "wellness", etc.
  selectedAt: timestamp("selected_at").notNull().defaultNow(),
  hasCompletedOnboarding: varchar("has_completed_onboarding", { length: 5 }).default("false"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertUserEditionSchema = createInsertSchema(userEditions).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertUserEdition = z.infer<typeof insertUserEditionSchema>;
export type UserEdition = typeof userEditions.$inferSelect;

// ============================================================================
// SUPER PROMPTS - User-created prompt templates
// ============================================================================

export const superPrompts = pgTable("super_prompts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  name: text("name").notNull(),
  description: text("description"),
  promptTemplate: text("prompt_template").notNull(),
  category: varchar("category", { length: 50 }).default("General"),
  icon: varchar("icon", { length: 50 }).default("Sparkles"),
  color: varchar("color", { length: 50 }).default("bg-purple-500"),
  variables: jsonb("variables").default(sql`'[]'::jsonb`),
  outputFormat: varchar("output_format", { length: 20 }).default("markdown"),
  tags: text("tags").array().default(sql`ARRAY[]::text[]`),
  isFavorite: varchar("is_favorite", { length: 5 }).default("false"),
  usageCount: varchar("usage_count").default("0"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertSuperPromptSchema = createInsertSchema(superPrompts).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertSuperPrompt = z.infer<typeof insertSuperPromptSchema>;
export type SuperPrompt = typeof superPrompts.$inferSelect;

// Web Search Citation Types (for OpenRouter web search responses)
export interface WebSearchCitation {
  type: "url_citation";
  url_citation: {
    url: string;
    title?: string;
    content?: string;
    start_index?: number;
    end_index?: number;
  };
}

export interface ChatCompletionChunk {
  content?: string;
  citations?: WebSearchCitation[];
  images?: Array<{ url: string; detail?: string }>;
}

// SSE Event types for streaming with citations and images
export type ChatStreamEvent =
  | { type: "delta"; content: string }
  | { type: "citations"; citations: WebSearchCitation[] }
  | { type: "images"; images: Array<{ url: string; detail?: string }> }
  | { type: "error"; error: string }
  | { type: "done" };

// ============================================================================
// SPACE TEMPLATES - Pre-built and custom space templates ("DEEPLY SMART [Product]")
// ============================================================================

// Template data structures for JSONB storage
export interface SpaceTemplateTopicConfig {
  name: string;
  icon: string;
  description?: string;
  color?: string;
}

export interface SpaceTemplateAgentConfig {
  name: string;
  description: string;
  persona?: string;
  systemPrompt?: string;
  capabilities?: string[];
  icon?: string;
}

export interface SpaceTemplatePromptConfig {
  name: string;
  description?: string;
  template: string;
  category?: string;
  variables?: string[];
  icon?: string;
}

export interface SpaceTemplateSourceConfig {
  title: string;
  type: "document" | "url" | "guide" | "checklist";
  content?: string;
  url?: string;
}

export interface SpaceTemplateTagConfig {
  name: string;
  color?: string;
}

export interface SpaceTemplateData {
  topics?: SpaceTemplateTopicConfig[];
  agents?: SpaceTemplateAgentConfig[];
  prompts?: SpaceTemplatePromptConfig[];
  sources?: SpaceTemplateSourceConfig[];
  tags?: SpaceTemplateTagConfig[];
  lens?: {
    purpose?: string;
    priorities?: string[];
    details?: string;
  };
  goals?: string[];
}

// Workflow engine tables (see supabase_migrations/062_workflow_engine.sql)
export const workerProfiles = pgTable("worker_profiles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id"),
  name: text("name").notNull(),
  description: text("description"),
  preferredModel: text("preferred_model"),
  fallbackModels: text("fallback_models").array().default(sql`ARRAY[]::text[]`),
  temperature: integer("temperature"),
  maxTokens: integer("max_tokens"),
  reasoningMode: boolean("reasoning_mode").default(false),
  creativity: text("creativity").default("balanced"),
  determinism: text("determinism").default("balanced"),
  speedTier: text("speed_tier").default("balanced"),
  budgetPolicy: jsonb("budget_policy").default(sql`'{}'::jsonb`),
  contextWindowHint: text("context_window_hint"),
  isBuiltin: boolean("is_builtin").default(false),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const workflowVersions = pgTable("workflow_versions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  workflowId: varchar("workflow_id").notNull(),
  userId: varchar("user_id").notNull(),
  versionNumber: integer("version_number").notNull(),
  nodes: jsonb("nodes").notNull().default(sql`'[]'::jsonb`),
  edges: jsonb("edges").notNull().default(sql`'[]'::jsonb`),
  entryNodeId: text("entry_node_id"),
  trigger: jsonb("trigger").notNull().default(sql`'{"type":"manual","config":{}}'::jsonb`),
  variables: jsonb("variables"),
  changelog: text("changelog"),
  publishedAt: timestamp("published_at").notNull().defaultNow(),
});

export const workflowRuns = pgTable("workflow_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").notNull(),
  workflowId: varchar("workflow_id").notNull(),
  workflowVersionId: varchar("workflow_version_id"),
  status: text("status").notNull().default("pending"),
  triggerType: text("trigger_type"),
  triggerPayload: jsonb("trigger_payload").default(sql`'{}'::jsonb`),
  currentNodeId: text("current_node_id"),
  state: jsonb("state").default(sql`'{}'::jsonb`),
  policySnapshot: jsonb("policy_snapshot").default(sql`'{}'::jsonb`),
  error: text("error"),
  startedAt: timestamp("started_at"),
  completedAt: timestamp("completed_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const workflowRunEvents = pgTable("workflow_run_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  runId: varchar("run_id").notNull(),
  nodeId: text("node_id"),
  nodeType: text("node_type"),
  eventType: text("event_type").notNull(),
  inputSnapshot: jsonb("input_snapshot"),
  outputSnapshot: jsonb("output_snapshot"),
  confidence: integer("confidence"),
  quality: integer("quality"),
  validationResult: jsonb("validation_result"),
  durationMs: integer("duration_ms"),
  actor: text("actor").notNull().default("system"),
  parallelBranchId: text("parallel_branch_id"),
  metadata: jsonb("metadata").default(sql`'{}'::jsonb`),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const workflowCheckpoints = pgTable("workflow_checkpoints", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  runId: varchar("run_id").notNull(),
  nodeId: text("node_id").notNull(),
  checkpointType: text("checkpoint_type").notNull(),
  outputOverride: jsonb("output_override"),
  chatThreadId: text("chat_thread_id"),
  resolvedAt: timestamp("resolved_at"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const workflowArtifacts = pgTable("workflow_artifacts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  runId: varchar("run_id").notNull(),
  nodeId: text("node_id"),
  userId: varchar("user_id").notNull(),
  artifactType: text("artifact_type").notNull(),
  threadId: text("thread_id"),
  kbDocumentId: text("kb_document_id"),
  clickupTaskId: text("clickup_task_id"),
  gmailId: text("gmail_id"),
  externalId: text("external_id"),
  title: text("title"),
  url: text("url"),
  metadata: jsonb("metadata").default(sql`'{}'::jsonb`),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const organizationPolicies = pgTable("organization_policies", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id"),
  workspaceId: varchar("workspace_id"),
  scope: text("scope").notNull().default("workspace"),
  allowedModels: text("allowed_models").array().default(sql`ARRAY[]::text[]`),
  security: jsonb("security").default(sql`'{}'::jsonb`),
  piiRules: jsonb("pii_rules").default(sql`'{}'::jsonb`),
  retention: jsonb("retention").default(sql`'{}'::jsonb`),
  costLimits: jsonb("cost_limits").default(sql`'{}'::jsonb`),
  approvalThreshold: jsonb("approval_threshold").default(sql`'{}'::jsonb`),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const spaceTemplates = pgTable("space_templates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  slug: varchar("slug", { length: 100 }).notNull().unique(),
  name: text("name").notNull(),
  description: text("description"),
  tagline: text("tagline"), // Short marketing tagline
  icon: varchar("icon", { length: 50 }).default("Sparkles"),
  color: varchar("color", { length: 50 }).default("bg-primary"),
  category: varchar("category", { length: 50 }).default("General"),
  tier: varchar("tier", { length: 20 }).default("free"), // free, pro, premium
  templateData: jsonb("template_data").default(sql`'{}'::jsonb`), // SpaceTemplateData
  isSystem: varchar("is_system", { length: 5 }).default("false"), // System templates vs user-created
  isFeatured: varchar("is_featured", { length: 5 }).default("false"),
  isPublished: varchar("is_published", { length: 5 }).default("true"),
  createdByUserId: varchar("created_by_user_id"), // null for system templates
  usageCount: varchar("usage_count").default("0"),
  version: varchar("version", { length: 20 }).default("1.0.0"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
});

export const insertSpaceTemplateSchema = createInsertSchema(spaceTemplates).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertSpaceTemplate = z.infer<typeof insertSpaceTemplateSchema>;
export type SpaceTemplate = typeof spaceTemplates.$inferSelect;
